import React, { useRef, useEffect } from 'react';

const ParticlesBackground = () => {
    const canvasRef = useRef(null);
    const mouseRef = useRef({ x: null, y: null });

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let particles = [];

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initParticles();
        };

        const initParticles = () => {
            particles = [];
            // "Más partículas" and "rubro de negocio" (Liquid/Gas/Fuel)
            // Dense field of particles to simulate fluid/gas density
            const particleCount = Math.min(Math.floor(window.innerWidth * window.innerHeight / 9000), 200);

            for (let i = 0; i < particleCount; i++) {
                const isGas = Math.random() > 0.7; // 30% Gas (Blue), 70% Oil (Amber)
                particles.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    vx: (Math.random() - 0.5) * 0.5,
                    vy: (Math.random() * -1.5) - 0.2, // Always rising (Gas/Oil bubbles)
                    size: Math.random() * 3 + 1,
                    color: isGas
                        ? `rgba(56, 189, 248, ${Math.random() * 0.4 + 0.1})` // Light Blue (Gas)
                        : `rgba(245, 158, 11, ${Math.random() * 0.4 + 0.1})`, // Amber (Oil)
                    originalVx: (Math.random() - 0.5) * 0.5,
                    originalVy: (Math.random() * -1.5) - 0.2,
                });
            }
        };

        const drawParticles = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];

                // Interaction with Mouse
                if (mouseRef.current.x != null) {
                    const dx = mouseRef.current.x - p.x;
                    const dy = mouseRef.current.y - p.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const forceDirectionX = dx / distance;
                    const forceDirectionY = dy / distance;
                    const force = (150 - distance) / 150;

                    if (distance < 150) {
                        p.vx -= forceDirectionX * force * 0.5;
                        p.vy -= forceDirectionY * force * 0.5;
                    }
                }

                // Apply velocity and friction for return to normal
                p.x += p.vx;
                p.y += p.vy;

                // Return to original speed slowly
                p.vx += (p.originalVx - p.vx) * 0.05;
                p.vy += (p.originalVy - p.vy) * 0.05;

                // Reset when out of top bounds (Rising effect)
                if (p.y < -10) {
                    p.y = canvas.height + 10;
                    p.x = Math.random() * canvas.width;
                }
                // Wrap X
                if (p.x < 0) p.x = canvas.width;
                if (p.x > canvas.width) p.x = 0;

                // Draw Particle
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = p.color;
                ctx.fill();

                // Draw Connections (Viscosity effect - shorter range)
                for (let j = i + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const dx = p.x - p2.x;
                    const dy = p.y - p2.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < 100) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(255, 255, 255, ${0.1 * (1 - distance / 100)})`; // Faint white connections
                        ctx.lineWidth = 0.5;
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.stroke();
                    }
                }
            }

            animationFrameId = requestAnimationFrame(drawParticles);
        };

        // Mouse listeners
        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouseRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
        };
        const handleMouseLeave = () => {
            mouseRef.current = { x: null, y: null };
        };

        window.addEventListener('resize', resizeCanvas);
        // Canvas mouse events
        // Note: Canvas needs styling to ensure it receives events if it's behind content. 
        // With 'pointer-events-none' in className, it won't receive events.
        // We need to attach listeners to window or make canvas interactive.
        // If we want interactivity, we must remove pointer-events-none.
        // BUT, if we remove it, we can't click buttons behind it.
        // Solution: Attach mouse move to window for repulsion effect.
        window.addEventListener('mousemove', handleMouseMove);

        resizeCanvas();
        drawParticles();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none" />;
};

export default ParticlesBackground;
